import React, { useState, useMemo } from "react"
import { useQuery, useMutation } from "react-query"
import { useParams } from "react-router-dom"
import { Container, Grid, CircularProgress, Chip } from "@material-ui/core"
import Select from "react-select"
import TableContainer from "../components/TableContainer"
import { CustomModal } from "../components/modal"
import { Button } from "../components"
import { fetchProjects } from "../services/project"
import {
  fetchDeal,
  linkCampaign,
  unlinkCampaign,
  createPayment,
  deletePayment,
  uploadFile,
} from "../services/deal"
import moment from "moment"

const paymentStatusChip = {
  paid:     "chip-green",
  partial:  "chip-orange",
  awaiting: "chip-red",
}

const selectStyles = {
  control: base => ({ ...base, height: 42, minHeight: 42 }),
}

const emptyPaymentForm = { amount: "", method_id: null, proof_url: "", notes: "", proof_file_id: null, proof_file_name: "" }

function DealDetail() {
  const { id } = useParams()

  const [linkCampaignOpen, setLinkCampaignOpen] = useState(false)
  const [campaignSearch, setCampaignSearch] = useState("")
  const [campaignResults, setCampaignResults] = useState([])
  const [addPaymentOpen, setAddPaymentOpen] = useState(false)
  const [paymentForm, setPaymentForm] = useState(emptyPaymentForm)

  // Data

  const { data, isLoading, refetch } = useQuery(
    ["deal-detail", id],
    () => fetchDeal(id),
    { refetchOnWindowFocus: false }
  )

  const paymentMethodOptions = [
    { value: Number(process.env.REACT_APP_PAYMENT_METHOD_STRIPE), label: "Stripe" },
    { value: Number(process.env.REACT_APP_PAYMENT_METHOD_BANK_TRANSFER), label: "Bank Transfer" },
  ]

  // Campaign search

  const { mutate: searchCampaigns, isLoading: isSearching } = useMutation(
    () => fetchProjects(null, false, false, false, { name: campaignSearch, pageIndex: 0, pageSize: 10 }),
    {
      onSuccess: res => setCampaignResults(res?.projects?.data || []),
      onError: () => alert("Search failed"),
    }
  )

  // Link / unlink

  const { mutate: link } = useMutation(
    campaignId => linkCampaign(id, campaignId),
    {
      onSuccess: () => {
        setCampaignResults([])
        setCampaignSearch("")
        setLinkCampaignOpen(false)
        refetch()
      },
      onError: err => alert(err?.data?.message || "Failed to link campaign"),
    }
  )

  const { mutate: unlink } = useMutation(
    campaignId => unlinkCampaign(id, campaignId),
    {
      onSuccess: () => refetch(),
      onError: err => alert(err?.data?.message || "Failed to unlink campaign"),
    }
  )

  // Payments

  const { mutate: addPayment, isLoading: isAddingPayment } = useMutation(
    () => {
      const { _pendingFileName, proof_file_name, ...fields } = paymentForm
      return createPayment({ ...fields, deal_id: Number(id), currency_id: data?.deal?.currency_id })
    },
    {
      onSuccess: () => { setAddPaymentOpen(false); setPaymentForm(emptyPaymentForm); refetch() },
      onError: err => alert(err?.data?.message || "Failed to add payment"),
    }
  )

  const { mutate: removePayment } = useMutation(deletePayment, {
    onSuccess: () => refetch(),
    onError: err => alert(err?.data?.message || "Failed to delete payment"),
  })

  const { mutate: uploadProof, isLoading: isUploadingProof } = useMutation(
    (file) => uploadFile(file),
    {
      onSuccess: (res) => setPaymentForm(f => ({ ...f, proof_file_id: res.id, proof_file_name: f._pendingFileName })),
      onError: () => alert("Failed to upload file"),
    }
  )

  // Columns

  const campaignColumns = useMemo(() => [
    { accessor: "id",          Header: "ID",       disableFilters: true },
    { accessor: "name",        Header: "Campaign", disableFilters: true },
    { accessor: "video_count", Header: "Videos",   disableFilters: true },
    {
      accessor: "revenue",
      Header: "Revenue",
      disableFilters: true,
      Cell: ({ value }) => <span>{data?.deal?.currency_symbol || ""}{Number(value || 0).toFixed(2)}</span>,
    },
    {
      accessor: "extra_cogs",
      Header: "COGS",
      disableFilters: true,
      Cell: ({ value }) => <span>{data?.deal?.currency_symbol || ""}{Number(value || 0).toFixed(2)}</span>,
    },
    {
      accessor: "start_date",
      Header: "Start",
      disableFilters: true,
      Cell: ({ value }) => value ? moment(value).format("ll") : "—",
    },
    {
      accessor: "end_date",
      Header: "End",
      disableFilters: true,
      Cell: ({ value }) => value ? moment(value).format("ll") : "—",
    },
    {
      id: "unlink",
      Header: "",
      disableFilters: true,
      Cell: ({ row }) => (
        <button
          className="stllr-btn btn-sm danger bordered"
          onClick={() => {
            if (window.confirm("Unlink this campaign from the deal?")) unlink(row.original.id)
          }}
        >
          Unlink
        </button>
      ),
    },
  ], [data])

  const paymentColumns = useMemo(() => [
    {
      accessor: "amount",
      Header: "Amount",
      disableFilters: true,
      Cell: ({ value }) => <span>{data?.deal?.currency_symbol || ""}{Number(value).toFixed(2)}</span>,
    },
    { accessor: "method_name", Header: "Method", disableFilters: true },
    {
      accessor: "proof_url",
      Header: "Proof",
      disableFilters: true,
      Cell: ({ row }) => {
        const { proof_file_url, proof_url } = row.original
        if (proof_file_url) return <a href={proof_file_url} target="_blank" rel="noopener noreferrer">View file</a>
        if (proof_url) return <span style={{ fontSize: 12, wordBreak: "break-all" }}>{proof_url}</span>
        return "—"
      },
    },
    {
      accessor: "notes",
      Header: "Notes",
      disableFilters: true,
      Cell: ({ value }) => value || "—",
    },
    {
      accessor: "date_add",
      Header: "Date",
      disableFilters: true,
      Cell: ({ value }) => moment(value).format("ll"),
    },
    {
      id: "del",
      Header: "",
      disableFilters: true,
      Cell: ({ row }) => (
        <button
          className="stllr-btn btn-sm danger bordered"
          onClick={() => {
            if (window.confirm("Delete this payment record?")) removePayment(row.original.id)
          }}
        >
          Delete
        </button>
      ),
    },
  ], [data])

  // Render

  if (isLoading) {
    return <div className="is-flex aic jcc mt-45"><CircularProgress /></div>
  }

  const { deal = {}, projects = [], payments = [] } = data || {}

  const totalRevenue  = Number(deal.revenue || 0)
  const totalCogs     = Number(deal.extra_cogs || 0)
  const sales         = Number(deal.sales || 0)
  const cashCollected = Number(deal.cash_collected || 0)
  const grossIncome   = sales - totalCogs
  const commissionPct = sales > 0 ? ((grossIncome / sales) * 100).toFixed(1) : "0.0"
  const startDate     = deal.start_date
  const endDate       = deal.end_date

  const summaryItems = [
    { label: "Start Date",     value: startDate ? moment(startDate).format("ll") : "—" },
    { label: "End Date",       value: endDate   ? moment(endDate).format("ll")   : "—" },
    { label: "Sales (agreed)", value: `${deal.currency_symbol || ""}${sales.toFixed(2)}`,          bold: true },
    { label: "Revenue",        value: `${deal.currency_symbol || ""}${totalRevenue.toFixed(2)}`  },
    { label: "COGS",           value: `${deal.currency_symbol || ""}${totalCogs.toFixed(2)}`     },
    { label: "Gross Income",   value: `${deal.currency_symbol || ""}${grossIncome.toFixed(2)}`   },
    { label: "Commission",     value: `${commissionPct}%`                                         },
    { label: "Cash Collected", value: `${deal.currency_symbol || ""}${cashCollected.toFixed(2)}`, bold: true },
  ]

  return (
    <Container spacing={1} maxWidth="xl">

      {/* Header */}
      <div className="is-flex aic jcsb mt-20 mb-20">
        <div>
          <h2 className="mt-0 mb-5">{deal.name}</h2>
          <span style={{ textTransform: "capitalize", color: "#888" }}>
            {deal.type} &middot; {deal.country_name} &middot; Created {moment(deal.date_add).format("ll")}
          </span>
        </div>
        <a className="stllr-btn primary bordered btn-md" href={`/deals/${id}/edit`}>
          Edit Deal
        </a>
      </div>

      {/* Summary strip */}
      <div className="group mb-30" style={{ background: "#f9f9f9", borderRadius: 8, padding: "20px 24px" }}>
        <Grid container spacing={2}>
          {summaryItems.map(({ label, value, bold }) => (
            <Grid item xs={6} sm={3} md={2} key={label}>
              <div className="teams-title mb-5">{label}</div>
              <div className={bold ? "fw7" : ""}>{value}</div>
            </Grid>
          ))}
          <Grid item xs={6} sm={3} md={2}>
            <div className="teams-title mb-5">Payment Status</div>
            <Chip
              size="small"
              className={paymentStatusChip[deal.payment_status] || "chip-gray"}
              label={deal.payment_status || "—"}
            />
          </Grid>
        </Grid>
      </div>

      {/* Linked campaigns */}
      <div className="group mb-30">
        <div className="is-flex aic jcsb mb-15">
          <h3 className="mt-0 mb-0">Linked Campaigns ({projects.length})</h3>
          <button className="stllr-btn primary btn-md" onClick={() => setLinkCampaignOpen(true)}>
            Link Campaign
          </button>
        </div>

        <TableContainer
          columns={campaignColumns}
          hiddenColumns={[]}
          data={projects}
          hasFilters={false}
          paging={true}
        />
      </div>

      {/* Payments */}
      <div className="group mb-30">
        <div className="is-flex aic jcsb mb-15">
          <h3 className="mt-0 mb-0">Payments ({payments.length})</h3>
          <button className="stllr-btn primary btn-md" onClick={() => setAddPaymentOpen(true)}>
            Add Payment
          </button>
        </div>

        <TableContainer
          columns={paymentColumns}
          hiddenColumns={[]}
          data={payments}
          hasFilters={false}
          paging={true}
        />
      </div>

      {/* Link campaign modal */}
      <CustomModal
        open={linkCampaignOpen}
        onClose={() => { setLinkCampaignOpen(false); setCampaignSearch(""); setCampaignResults([]) }}
        body={
          <div>
            <h3 className="mt-0 mb-20">Link Campaign</h3>

            <div className="is-flex aic mb-15">
              <input
                className="stllr-input"
                style={{ flex: 1, marginRight: 10 }}
                placeholder="Search campaign by name&hellip;"
                value={campaignSearch}
                onChange={e => setCampaignSearch(e.target.value)}
                onKeyDown={e => e.key === "Enter" && campaignSearch.trim() && searchCampaigns()}
              />
              <button
                className="stllr-btn primary bordered btn-md"
                disabled={isSearching || !campaignSearch.trim()}
                onClick={() => searchCampaigns()}
              >
                {isSearching ? "Searching..." : "Search"}
              </button>
            </div>

            {campaignResults.length > 0 && (
              <div style={{ border: "1px solid #e0e0e0", borderRadius: 6, padding: 12 }}>
                <div className="teams-title mb-10">Results — click Link to add to this deal</div>
                {campaignResults.map(c => (
                  <div
                    key={c.id}
                    className="is-flex aic jcsb mb-5 pb-5"
                    style={{ borderBottom: "1px solid #f0f0f0" }}
                  >
                    <span>
                      {c.name}
                      <span style={{ color: "#aaa", fontSize: 12, marginLeft: 6 }}>#{c.id}</span>
                    </span>
                    <button className="stllr-btn btn-sm primary bordered" onClick={() => link(c.id)}>
                      Link
                    </button>
                  </div>
                ))}
              </div>
            )}

            {campaignResults.length === 0 && campaignSearch && !isSearching && (
              <div style={{ color: "#aaa", textAlign: "center", padding: "20px 0" }}>No results found</div>
            )}
          </div>
        }
      />

      {/* Add payment modal */}
      <CustomModal
        open={addPaymentOpen}
        onClose={() => { setAddPaymentOpen(false); setPaymentForm(emptyPaymentForm) }}
        body={
          <div>
            <h3 className="mt-0 mb-20">Add Payment</h3>

            <div className="group is-flex fdr mb-15">
              <div className="group" style={{ width: "50%" }}>
                <div className="teams-title mb-10">Amount* ({deal.currency_symbol || ""})</div>
                <input
                  type="number"
                  className="stllr-input"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={paymentForm.amount}
                  onChange={e => setPaymentForm(f => ({ ...f, amount: e.target.value }))}
                />
              </div>
              <div className="group ml-20" style={{ width: "50%" }}>
                <div className="teams-title mb-10">Method*</div>
                <Select
                  options={paymentMethodOptions}
                  value={paymentMethodOptions.find(o => o.value === paymentForm.method_id) || null}
                  placeholder="Select method"
                  onChange={({ value }) => setPaymentForm(f => ({ ...f, method_id: value }))}
                  styles={selectStyles}
                />
              </div>
            </div>

            <div className="group mb-15">
              <div className="teams-title mb-10">Proof Screenshot (optional)</div>
              {paymentForm.proof_file_id ? (
                <div className="is-flex aic" style={{ gap: 10 }}>
                  <span style={{ fontSize: 13, color: "#444" }}>✓ {paymentForm.proof_file_name}</span>
                  <button
                    className="stllr-btn btn-sm danger bordered"
                    onClick={() => setPaymentForm(f => ({ ...f, proof_file_id: null, proof_file_name: "" }))}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="is-flex aic" style={{ gap: 10 }}>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    style={{ display: "none" }}
                    id="proof-file-input"
                    onChange={e => {
                      const file = e.target.files[0]
                      if (!file) return
                      setPaymentForm(f => ({ ...f, _pendingFileName: file.name }))
                      uploadProof(file)
                      e.target.value = ""
                    }}
                  />
                  <label
                    htmlFor="proof-file-input"
                    className="stllr-btn secondary bordered btn-md"
                    style={{ cursor: "pointer", margin: 0 }}
                  >
                    {isUploadingProof ? "Uploading..." : "Upload Screenshot"}
                  </label>
                  {isUploadingProof && <span style={{ fontSize: 12, color: "#888" }}>Please wait...</span>}
                </div>
              )}
            </div>

            <div className="group mb-15">
              <div className="teams-title mb-10">Proof URL / Reference (optional)</div>
              <input
                className="stllr-input"
                placeholder="e.g. Stripe payment intent ID, bank ref..."
                value={paymentForm.proof_url}
                onChange={e => setPaymentForm(f => ({ ...f, proof_url: e.target.value }))}
              />
            </div>

            <div className="group mb-20">
              <div className="teams-title mb-10">Notes (optional)</div>
              <input
                className="stllr-input"
                placeholder="Any additional notes"
                value={paymentForm.notes}
                onChange={e => setPaymentForm(f => ({ ...f, notes: e.target.value }))}
              />
            </div>

            <div className="is-flex aic jcfe">
              <Button
                text="Cancel"
                className="secondary bordered btn-md mr-10"
                onClick={() => { setAddPaymentOpen(false); setPaymentForm(emptyPaymentForm) }}
                disabled={isAddingPayment}
              />
              <Button
                text="Add Payment"
                className="primary btn-md"
                disabled={isAddingPayment || isUploadingProof || !paymentForm.amount || !paymentForm.method_id}
                onClick={() => addPayment()}
              />
            </div>
          </div>
        }
      />

    </Container>
  )
}

export default DealDetail
